import React, {
  useState,
  useCallback,
  useEffect,
  DragEvent,
  MouseEvent,
  RefObject,
  useRef,
} from "react";
import { useDispatch, useStore } from "react-redux";
import { SetSelection } from "../../../lib/lib";
import { syncEditorToolbarFromSelection } from "../../../lib/editorToolbarSync";
import { RootState } from "../../../reducers";
type Props = {
  ScreenRef: RefObject<HTMLDivElement>;
  SaveContent: () => void;
};
function Screen({ ScreenRef, SaveContent }: Props) {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toolbarSyncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [DragData, setDragData] = useState<any>(undefined);
  const [clientY, setClientY] = useState<number | undefined>(undefined);
  const [youtube, setYoutube] = useState<undefined | HTMLElement>(undefined);

  const __saveSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    if (!range.startContainer || !range.endContainer) return;
    SetSelection(range);
  }, []);

  useEffect(() => {
    const scheduleToolbarSync = (el: HTMLElement) => {
      if (toolbarSyncTimer.current) clearTimeout(toolbarSyncTimer.current);
      toolbarSyncTimer.current = setTimeout(() => {
        toolbarSyncTimer.current = null;
        syncEditorToolbarFromSelection(
          dispatch,
          el,
          store.getState().editor
        );
      }, 50);
    };

    const handler = () => {
      const el = ScreenRef.current;
      if (!el) return;

      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (!el.contains(range.commonAncestorContainer)) return;

      scheduleToolbarSync(el);

      const active = document.activeElement;
      if (active && !el.contains(active)) return;

      SetSelection(range);
    };

    document.addEventListener("selectionchange", handler);
    return () => {
      document.removeEventListener("selectionchange", handler);
      if (toolbarSyncTimer.current) clearTimeout(toolbarSyncTimer.current);
    };
  }, [ScreenRef, dispatch, store]);

  const __cursorMove = useCallback((e: any) => {
    const el = e.target;
    const selection = window.getSelection();
    
    // 현재 커서 위치가 있는 경우 그대로 유지
    if (selection && selection.rangeCount > 0) {
      const currentRange = selection.getRangeAt(0);
      const currentContainer = currentRange.startContainer;
      
      // 같은 요소 내에서만 커서 위치 유지
      if (el.contains(currentContainer) || el === currentContainer) {
        return;
      }
    }
    
    // 새로운 요소로 이동하는 경우에만 끝으로 이동
    const range = document.createRange();
    if (selection) {
      selection.removeAllRanges();
      range.selectNodeContents(el);
      range.collapse(false);
      selection.addRange(range);
      el.focus();
    }
  }, []);
  const __Dragover = useCallback(
    (e: DragEvent) => {
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = "move";
        // 드래그 중에는 커서 이동하지 않음
        // if (DragData) {
        //   __cursorMove(e);
        // }
      }
      e.stopPropagation();
      e.preventDefault();
    },
    [DragData]
  );
  const __DragStart = useCallback((e: DragEvent) => {
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      setDragData(e.target);
      setClientY(e.clientY);
    }
  }, []);

  const handleInput = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      SaveContent();
    }, 500); // 0.5초 동안 추가 입력 없을 때 실행
  }, [SaveContent]);

  const __MouseUp = useCallback(
    (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (youtube && e.target) {
        youtube.setAttribute("style", "top:unset;left:unset;");
        if (target.className !== "template-youtube lite") {
          if (target.className !== "editor-screen") {
            target.after(youtube);
          } else {
            if (clientY) {
              if (e.clientY > clientY) {
                target.appendChild(youtube);
              } else {
                target.insertBefore(youtube, target.firstChild);
              }
            }
          }
        } else {
          target.className = "template-youtube play";
        }
        setYoutube(undefined);
      }

      __saveSelection();
    },
    [youtube, clientY, __saveSelection]
  );
  const __MouseDown = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.className === "template-youtube lite") {
      target.setAttribute("style", "opacity:0.4;");
      setYoutube(target);
      setClientY(e.clientY);
    }
  }, []);
  const __Drop = useCallback(
    (e: DragEvent) => {
      if (DragData) {
        const target = e.target as HTMLElement;
        if (target.className !== "editor-screen") {
          target.after(DragData);
        } else {
          if (clientY) {
            if (e.clientY > clientY) {
              target.appendChild(DragData);
            } else {
              target.insertBefore(DragData, target.firstChild);
            }
          }
        }
        setDragData(undefined);
      }
    },
    [DragData, clientY]
  );

  return (
    <div
      id="screen"
      className="editor-screen"
      contentEditable="true"
      ref={ScreenRef}
      suppressContentEditableWarning={true}
      onBlur={(e) => {
        __saveSelection();
      }}
      onInput={handleInput}
      onKeyUp={__saveSelection}

      onDragOver={__Dragover}
      onDragStart={__DragStart}
      onMouseUp={__MouseUp}
      onMouseDown={__MouseDown}
      onDrop={__Drop}
    >
      <div></div>
    </div>
  );
}

export default React.memo(Screen);
